/**
 * One event-setup section as a compact, expandable row inside the onboarding
 * panel. Collapsed header: icon + title + a hover-only ✕ (skip / "I'll do it
 * myself") next to the chevron that opens/closes the row. Expanded: a short
 * text referencing what the user told Velo in the interview (the section
 * brief; falls back to a generic blurb) plus the lanes — Build it (Velo),
 * Adjust (section-scoped Velo chat). Only open sections (PROPOSED/BUILDING)
 * reach this row; resolved/skipped ones are filtered out upstream.
 */
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import { IconChevronRight, IconLoader2, IconX } from "@tabler/icons-react";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { TertiaryButton } from "@/components/Buttons/TertiaryButton";
import { SetupPlanSection, SetupSectionKey } from "@/types/SetupPlan";
import {
  BUILDABLE,
  MANUAL_CTAS,
  SECTION_ICONS,
  SECTION_ROUTES,
  VELO_ONLY,
} from "@/pages/event/setup/sectionMeta";

interface Props {
  eventId: string;
  section: SetupPlanSection;
  canWrite: boolean;
  workflowsBlocked: boolean;
  workflowsManualOnly: boolean;
  building: boolean;
  anyBuildInFlight: boolean;
  expanded: boolean;
  onToggle: () => void;
  onBuild: (key: SetupSectionKey) => void;
  onSkip: (key: SetupSectionKey) => void;
  onAdjust?: (key: SetupSectionKey) => void;
}

export function OnboardingSetupRow({
  eventId,
  section,
  canWrite,
  workflowsBlocked,
  workflowsManualOnly,
  building,
  anyBuildInFlight,
  expanded,
  onToggle,
  onBuild,
  onSkip,
  onAdjust,
}: Props) {
  const t = useTranslations("setupHub");
  const navigate = useNavigate();
  const { key, status } = section;

  const route = `/event/${eventId}/${SECTION_ROUTES[key]}`;
  const hasProposal = !!section.proposal?.trim();
  const manualOnly = key === "WORKFLOWS" && workflowsManualOnly;
  // A section Velo can set up conversationally (has a lane); the button always
  // routes there. Direct-build survives only as a fallback when Velo is off —
  // VELO_ONLY sections have no pack proposal, so their fallback is the manual
  // CTA (deep link) instead.
  const veloSection = (BUILDABLE.has(key) || VELO_ONLY.has(key)) && !manualOnly;
  const showVelo = veloSection && canWrite && !!onAdjust;
  const showBuildFallback =
    BUILDABLE.has(key) && !manualOnly && canWrite && !onAdjust && hasProposal;
  const buildBlocked = key === "WORKFLOWS" && workflowsBlocked;
  const serverBuilding = status === "BUILDING" && !building;
  const failedLinks = (section.resultLinks ?? []).filter((link) => !link.ok);
  const title = t(`sections.${key}.title`);

  return (
    <div className="rounded-lg">
      <div className="group flex items-center gap-1 rounded-lg pr-1.5 transition-colors hover:surface-tertiary">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-2.5 py-2 pl-2.5 text-left cursor-pointer"
        >
          <span className="shrink-0 flex h-[18px] w-[18px] items-center justify-center">
            {building || status === "BUILDING" ? (
              <IconLoader2 size={16} className="font-blue animate-spin" />
            ) : (
              <span className="font-tertiary">{SECTION_ICONS[key]}</span>
            )}
          </span>
          <span className="min-w-0 flex-1 truncate body-3-medium font-primary">{title}</span>
        </button>
        {/* "I'll do it myself" — hover-only ✕ that drops the section from the
            list (skip). Auto-detect flips it to done once the work exists. */}
        {canWrite && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSkip(key);
            }}
            className="shrink-0 inline-flex h-6 w-6 items-center justify-center rounded font-quaternary opacity-0 transition-opacity hover:font-primary group-hover:opacity-100 cursor-pointer"
            title={t("actions.manual")}
            aria-label={t("actions.manual")}
            data-testid={`setup-skip-${key.toLowerCase()}`}
          >
            <IconX size={14} />
          </button>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="shrink-0 inline-flex h-6 w-6 items-center justify-center cursor-pointer"
          aria-label={title}
        >
          <IconChevronRight
            size={14}
            className={`font-tertiary transition-transform ${expanded ? "rotate-90" : ""}`}
          />
        </button>
      </div>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-2.5 px-2.5 pb-3 pt-0.5">
              {/* Reference what the user actually asked for in the interview;
                  fall back to a generic one-liner when no brief was captured. */}
              {section.brief?.trim() ? (
                <p
                  className="line-clamp-4 border-l-2 border-l-(--color-outline-tertiary) pl-2.5 body-3-regular font-secondary"
                  title={section.brief}
                >
                  <span className="font-tertiary">{t("briefLabel")}: </span>
                  {section.brief}
                </p>
              ) : (
                <p className="body-3-regular font-tertiary">{t(`sections.${key}.blurb`)}</p>
              )}

              {BUILDABLE.has(key) && !manualOnly && !hasProposal && !showVelo && (
                <p className="body-3-regular font-quaternary">{t("emptyProposal")}</p>
              )}

              <div className="flex flex-wrap items-center gap-2">
                {/* One action, always Velo: it builds/adjusts this section
                    conversationally. Plain blue text link per the Velo-action
                    convention (no direct "build for me" button). */}
                {showVelo && (
                  <TertiaryButton
                    text={t("actions.withVelo")}
                    color="font-blue"
                    size="xsmall"
                    width="w-max"
                    onClick={() => onAdjust!(key)}
                    testId={`setup-velo-${key.toLowerCase()}`}
                  />
                )}
                {showBuildFallback && (
                  <PrimaryButton
                    text={
                      failedLinks.length > 0 && !building
                        ? t("actions.retryBuild")
                        : t("actions.build")
                    }
                    size="xsmall"
                    width="w-max"
                    loading={building}
                    disabled={building || anyBuildInFlight || buildBlocked || serverBuilding}
                    onClick={() => onBuild(key)}
                    testId={`setup-build-${key.toLowerCase()}`}
                  />
                )}
                {/* Manual deep link — for VELO_ONLY sections only as the
                    fallback while the Velo lane is unavailable. */}
                {MANUAL_CTAS[key] && !showVelo && (
                  <PrimaryButton
                    text={t(MANUAL_CTAS[key]!.labelKey)}
                    icon={MANUAL_CTAS[key]!.icon}
                    iconSize={14}
                    size="xsmall"
                    width="w-max"
                    onClick={() => navigate(route)}
                  />
                )}
                {buildBlocked && !manualOnly && (
                  <span className="w-full body-3-regular font-quaternary">
                    {t("buildEmailsFirst")}
                  </span>
                )}
                {manualOnly && (
                  <span className="w-full body-3-regular font-quaternary">
                    {t("workflowsManualHint")}
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
